/**
 * Single source of truth for Terac company information.
 *
 * Import these constants instead of hardcoding strings like addresses,
 * phone numbers, or contact emails. `.md` legal documents reference
 * these via `{{COMPANY.*}}` placeholders resolved by
 * `resolveCompanyPlaceholders()` at render time.
 */

export interface PostalAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export const COMPANY_LEGAL_NAME = "Terac Inc.";
export const COMPANY_DISPLAY_NAME = "Terac";
export const COMPANY_WEBSITE = "https://terac.com";

export const COMPANY_MAILING_ADDRESS: PostalAddress = {
  street: "605 Market Street",
  city: "San Francisco",
  state: "CA",
  zip: "94105",
  country: "United States",
};

export const COMPANY_REGISTRATION_ADDRESS: PostalAddress = {
  street: "1721 Mable Avenue",
  city: "Modesto",
  state: "CA",
  zip: "95355",
  country: "United States",
};

export const COMPANY_EMAILS = {
  support: "support@terac.com",
  privacy: "privacy@terac.com",
  privacyOfficer: "privacy-officer@terac.com",
  compliance: "compliance@terac.com",
  security: "security@terac.com",
  legal: "legal@terac.com",
  careers: "careers@terac.com",
  help: "support@terac.com",
  contact: "zac@terac.com",
  // Destination for /contact inquiry-form submissions (quote/sales triage).
  quotes: "sales@terac.com",
  // The client RFQ Google Group: inbound quote threads arrive addressed here
  // and our feasibility replies CC it so the team sees them in the group.
  quotesGroup: "quotes@terac.com",
} as const;

/**
 * Teammates copied on the inbound sales forms. Kept out of COMPANY_EMAILS
 * because those keys are addresses we publish (the legal documents render
 * `contact`), while this list only ever widens a notification `to:`.
 */
const SALES_TEAM_EMAILS = ["gabriella@terac.com"] as const;

/** `to:` for /contact inquiry-form submissions. */
export const SALES_INQUIRY_RECIPIENTS: readonly string[] = [
  COMPANY_EMAILS.quotes,
  ...SALES_TEAM_EMAILS,
];

/** `to:` for project-request submissions. */
export const PROJECT_REQUEST_RECIPIENTS: readonly string[] = [
  COMPANY_EMAILS.contact,
  ...SALES_TEAM_EMAILS,
];

export const COMPANY_PHONE = {
  raw: "+17034051525",
  displayParens: "(+1)7034051525",
  displayDashed: "+1 (703) 405-1525",
} as const;

export function formatAddressOneLine(address: PostalAddress): string {
  return `${address.street}, ${address.city}, ${address.state} ${address.zip}, ${address.country}`;
}

export const COMPANY_MAILING_ADDRESS_ONE_LINE = formatAddressOneLine(
  COMPANY_MAILING_ADDRESS,
);

export const COMPANY_REGISTRATION_ADDRESS_ONE_LINE = formatAddressOneLine(
  COMPANY_REGISTRATION_ADDRESS,
);

/**
 * Multi-line postal block suitable for both Markdown and plain text.
 * Uses trailing "  \n" (two-space + newline) which is a CommonMark hard
 * break (renders as `<br>` in `react-markdown`). In plain-text contexts
 * (email templates, logs) the trailing spaces are harmless.
 */
export function formatAddressBlock(address: PostalAddress): string {
  return `${address.street}  \n${address.city}, ${address.state} ${address.zip}  \n${address.country}`;
}

export const COMPANY_MAILING_ADDRESS_BLOCK = formatAddressBlock(
  COMPANY_MAILING_ADDRESS,
);

/**
 * Placeholder map for rendering company info into Markdown/MDX legal
 * documents. Keep in sync with the {{COMPANY.*}} tokens used in
 * `apps/web/src/app/(marketing)/legal/**\/*.md`.
 */
export const COMPANY_PLACEHOLDERS: Record<string, string> = {
  "COMPANY.LEGAL_NAME": COMPANY_LEGAL_NAME,
  "COMPANY.DISPLAY_NAME": COMPANY_DISPLAY_NAME,
  "COMPANY.WEBSITE": COMPANY_WEBSITE,
  "COMPANY.PHONE": COMPANY_PHONE.displayDashed,
  "COMPANY.EMAIL.SUPPORT": COMPANY_EMAILS.support,
  "COMPANY.EMAIL.PRIVACY": COMPANY_EMAILS.privacy,
  "COMPANY.EMAIL.PRIVACY_OFFICER": COMPANY_EMAILS.privacyOfficer,
  "COMPANY.EMAIL.COMPLIANCE": COMPANY_EMAILS.compliance,
  "COMPANY.EMAIL.SECURITY": COMPANY_EMAILS.security,
  "COMPANY.EMAIL.LEGAL": COMPANY_EMAILS.legal,
  "COMPANY.EMAIL.CAREERS": COMPANY_EMAILS.careers,
  "COMPANY.EMAIL.HELP": COMPANY_EMAILS.help,
  "COMPANY.EMAIL.CONTACT": COMPANY_EMAILS.contact,
  "COMPANY.ADDRESS.STREET": COMPANY_MAILING_ADDRESS.street,
  "COMPANY.ADDRESS.CITY": COMPANY_MAILING_ADDRESS.city,
  "COMPANY.ADDRESS.STATE": COMPANY_MAILING_ADDRESS.state,
  "COMPANY.ADDRESS.ZIP": COMPANY_MAILING_ADDRESS.zip,
  "COMPANY.ADDRESS.COUNTRY": COMPANY_MAILING_ADDRESS.country,
  "COMPANY.ADDRESS.ONE_LINE": COMPANY_MAILING_ADDRESS_ONE_LINE,
  "COMPANY.ADDRESS.BLOCK": COMPANY_MAILING_ADDRESS_BLOCK,
  "COMPANY.REGISTRATION_ADDRESS.ONE_LINE":
    COMPANY_REGISTRATION_ADDRESS_ONE_LINE,
};

const PLACEHOLDER_PATTERN = /\{\{\s*([A-Z][A-Z0-9_.]*)\s*\}\}/g;

/**
 * Replace `{{COMPANY.*}}` tokens in a string with live values. Unknown
 * placeholders are left untouched so typos surface during review rather
 * than silently rendering as blank.
 *
 * Intended for trusted, repo-checked-in content only (e.g. legal `.md`
 * files loaded via `readFileSync`). Do NOT route user-supplied input
 * through this because a crafted placeholder could inject brand values into
 * content that's later rendered verbatim.
 */
export function resolveCompanyPlaceholders(input: string): string {
  return input.replace(PLACEHOLDER_PATTERN, (match, key: string) => {
    const value = COMPANY_PLACEHOLDERS[key];
    return value ?? match;
  });
}
